import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConversations, getConversation, getConversationMessages, sendMessage, markConversationAsRead, sendTypingPresence, sendReaction, deleteMessage, sendMediaMessage, refreshContactAvatar, downloadMessageMedia, getWhatsappCatalog, sendStickerMessage } from '../services/apiContacts';
import ContactModal from '../components/contacts/ContactModal';
import StickerPicker from '../components/chat/StickerPicker';
import { getMessageTemplates } from '../services/apiMessageTemplates';
import { useAuthStore } from '../store/useAuthStore';
import { useNotificationStore } from '../store/useNotificationStore';
import { useModules } from '../lib/useModules';
import echo from '../lib/echo';
import Header from '../components/layout/Header';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es'; // Importar locale español
import { resolveMediaUrl } from '../lib/utils';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es'); // Configurar locale global

const formatChatDate = (dateString) => {
    const date = dayjs(dateString);
    const now = dayjs();
    
    if (date.isSame(now, 'day')) return 'Hoy';
    if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Ayer';
    
    if (now.diff(date, 'day') < 7) {
        return date.format('dddd'); // Lunes, Martes...
    }
    
    return date.format('D [de] MMMM'); // 15 de mayo
};

const formatJidToPhone = (jid) => {
    if (!jid) return '';
    const cleanJid = jid.split('@')[0];
    return cleanJid.startsWith('+') ? cleanJid : `+${cleanJid}`;
};

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export default function ChatPage() {
    const { user } = useAuthStore();
    const { isFeatureEnabled } = useModules();

    const canSendMedia = isFeatureEnabled('chat', 'send_media');
    const canUseVoiceNotes = isFeatureEnabled('chat', 'voice_notes');
    const canReact = isFeatureEnabled('chat', 'reactions');
    const canDeleteMessages = isFeatureEnabled('chat', 'delete_messages');
    const canUseCatalog = isFeatureEnabled('chat', 'catalog');
    const canUseStickers = isFeatureEnabled('chat', 'stickers');

    const { clearConversationNotifications } = useNotificationStore();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [replyingTo, setReplyingTo] = useState(null);
    const replyingToRef = useRef(null);

    // WhatsApp Catalog Drawer States
    const [showCatalogDrawer, setShowCatalogDrawer] = useState(false);
    const [showStickers, setShowStickers] = useState(false);
    const [stickerError, setStickerError] = useState(null);
    const [enviandoSticker, setEnviandoSticker] = useState(false);
    const [catalogQuery, setCatalogQuery] = useState('');
    const [sendingProductId, setSendingProductId] = useState(null);

    // Delete Message States & Handler
    const [deletingMessage, setDeletingMessage] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirmDelete = useCallback(async (forEveryone) => {
        if (!deletingMessage || !activeConversationId) return;

        const msgToDelete = deletingMessage;
        setIsDeleting(true);

        // Optimistic UI update — remove message from cache immediately
        queryClient.setQueryData(['messages', activeConversationId], (oldData) => {
            if (!oldData) return oldData;
            const messagesList = Array.isArray(oldData.data) ? oldData.data : (Array.isArray(oldData) ? oldData : []);
            const updatedList = messagesList.filter(m => m.id !== msgToDelete.id);
            return Array.isArray(oldData.data) ? { ...oldData, data: updatedList } : updatedList;
        });

        try {
            await deleteMessage(activeConversationId, msgToDelete.id, forEveryone);
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
        } catch (err) {
            console.error('Delete message failed:', err);
            // Revert cache on error
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
            alert('No se pudo eliminar el mensaje. Intenta de nuevo.');
        } finally {
            setIsDeleting(false);
            setDeletingMessage(null);
        }
    }, [deletingMessage, activeConversationId, queryClient]);

    useEffect(() => {
        replyingToRef.current = replyingTo;
    }, [replyingTo]);

    const scrollToMessage = useCallback((msgId) => {
        const element = document.getElementById(`msg-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('animate-flash-highlight');
            setTimeout(() => {
                element.classList.remove('animate-flash-highlight');
            }, 1500);
        }
    }, []);

    const messagesEndRef = useRef(null);
    const pollingRef = useRef(null);
    const typingDebounceRef = useRef(null); // Anti-ban: previene spam de eventos "composing"
    const [hoveredMessageId, setHoveredMessageId] = useState(null); // Para mostrar emoji picker
    const [selectedReactions, setSelectedReactions] = useState({}); // { [messageId]: emoji }
    const hoverTimeoutRef = useRef(null);
    
    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all' | 'unread'
    
    // Pagination & Hover Tooltip states
    const [conversationsLimit, setConversationsLimit] = useState(25);
    const [hoveredConvId, setHoveredConvId] = useState(null);
    const [hoveredRect, setHoveredRect] = useState(null);

    // Reset pagination when search/filter changes
    useEffect(() => {
        setConversationsLimit(25);
    }, [searchTerm, filterType]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        // Check if user has scrolled to the bottom (within 20px)
        if (scrollHeight - scrollTop - clientHeight < 20) {
            setConversationsLimit(prev => prev + 10);
        }
    };

    // Template Suggestions states
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [suggestionIndex, setSuggestionIndex] = useState(0);
    const [suggestionQuery, setSuggestionQuery] = useState('');
    const textareaRef = useRef(null);
    const menuRef = useRef(null);

    const navigate = useNavigate();

    const [showMenu, setShowMenu] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    const { data: allTemplates } = useQuery({
        queryKey: ['message-templates'],
        queryFn: async () => {
            const res = await getMessageTemplates();
            return res.data;
        },
        enabled: !!user,
    });

    const handleMouseEnter = useCallback((id) => {
        clearTimeout(hoverTimeoutRef.current);
        setHoveredMessageId(id);
    }, []);

    const handleMouseLeave = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredMessageId(null);
        }, 300);
    }, []);

    // Media states
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const isRecordingRef = useRef(false);
    const recordingTimerVal = useRef(0);
    
    const fileInputRef = useRef(null);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);

    // Track which video message IDs are currently being downloaded (set of ids)
    const [downloadingVideoIds, setDownloadingVideoIds] = useState(new Set());

    // Emojis disponibles para reaccionar — los 6 más usados en WhatsApp
    const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

    // Handler de reacción: muestra feedback inmediato y envía en background
    const handleReaction = useCallback((msg, emoji) => {
        // Optimistic update — muestra el emoji inmediatamente
        setSelectedReactions(prev => ({ ...prev, [msg.id]: emoji }));
        setHoveredMessageId(null); // Cierra el picker
        // Disparar a la API en background (fire-and-forget)
        sendReaction(activeConversationId, msg.id, emoji);
    }, [activeConversationId]);

    /**
     * Handle on-demand video download.
     * Shows a spinner on the card while downloading, then refreshes the messages
     * query so the video player appears automatically.
     */
    const handleVideoDownload = useCallback(async (msg) => {
        if (downloadingVideoIds.has(msg.id)) return; // Prevent double-click

        setDownloadingVideoIds(prev => new Set(prev).add(msg.id));
        try {
            await downloadMessageMedia(msg.id);
            // Refresh messages — the updated message will now have media_url set
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
        } catch (err) {
            console.error('Video download failed:', err);
            // Notify the user with a non-blocking toast-style alert
            alert('No se pudo descargar el video. Por favor intenta de nuevo.');
        } finally {
            setDownloadingVideoIds(prev => {
                const next = new Set(prev);
                next.delete(msg.id);
                return next;
            });
        }
    }, [downloadingVideoIds, activeConversationId, queryClient]);

    // Anti-ban: Disparar presencia "composing" cuando el vendedor escribe.
    // Debounced a 4s para no spamear el endpoint y coincidir con la duración en el backend.
    const handleTyping = useCallback(() => {
        if (!activeConversationId) return;
        if (typingDebounceRef.current) return; // Ya enviamos presencia recientemente

        sendTypingPresence(activeConversationId); // Fire-and-forget

        // Bloquear llamadas duplicadas con un tiempo aleatorio (3.5s a 6s)
        const nextDelay = Math.floor(Math.random() * (6000 - 3500 + 1)) + 3500;
        typingDebounceRef.current = setTimeout(() => {
            typingDebounceRef.current = null;
        }, nextDelay);
    }, [activeConversationId]);

    // Mark conversation as read when selected
    useEffect(() => {
        if (!activeConversationId) return;

        const performMarkAsRead = async () => {
            try {
                await markConversationAsRead(activeConversationId);
                // Clear header notifications locally for this conversation
                clearConversationNotifications(activeConversationId);
                // Invalidate conversations list to update unread counts
                queryClient.invalidateQueries({ queryKey: ['conversations'] });
            } catch (err) {
                console.error('Failed to mark conversation as read:', err);
            }
        };

        performMarkAsRead();
    }, [activeConversationId, queryClient, clearConversationNotifications]);

    // Fetch conversations list — include search, filters and limit
    const { data: conversationsResponse, isLoading: convsLoading } = useQuery({
        queryKey: ['conversations', searchTerm.length >= 3 ? searchTerm : '', filterType, conversationsLimit],
        queryFn: () => getConversations({
            search: searchTerm.length >= 3 ? searchTerm : '',
            only_unread: filterType === 'unread' ? 1 : 0,
            per_page: conversationsLimit
        }),
        refetchInterval: 15000,
        refetchIntervalInBackground: true,
        placeholderData: (prev) => prev,
    });
    
    const conversations = Array.isArray(conversationsResponse?.data) ? conversationsResponse.data : [];
    const activeConversationFromList = conversations.find(c => c.id === activeConversationId);

    // Fetch single conversation details if activeConversationId is not in the paginated list
    const { data: singleConvData } = useQuery({
        queryKey: ['conversation', activeConversationId],
        queryFn: () => getConversation(activeConversationId),
        enabled: !!activeConversationId && !activeConversationFromList,
        staleTime: 30000,
    });

    const activeConversation = activeConversationFromList || singleConvData?.data || (singleConvData?.id ? singleConvData : null);

    // Auto-select conversation from ?conversation= query param (e.g. from Vista 360 "Abrir chat" link)
    useEffect(() => {
        const paramId = searchParams.get('conversation');
        if (paramId) {
            const numericId = parseInt(paramId, 10);
            if (!isNaN(numericId)) {
                setActiveConversationId(numericId);
            }
            // Clean the URL without triggering a navigation
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Fetch messages — refetch every 2s to keep it tightly in sync with the sidebar
    const { data: messagesResponse, isLoading: msgsLoading } = useQuery({
        queryKey: ['messages', activeConversationId],
        queryFn: () => getConversationMessages(activeConversationId),
        enabled: !!activeConversationId,
        refetchInterval: 6000,
        refetchIntervalInBackground: true,
    });
    
    const messages = Array.isArray(messagesResponse?.data) ? messagesResponse.data : [];

    // Utility to scroll to the bottom of the chat pane
    const scrollToBottom = useCallback((behavior = 'smooth') => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior });
        }
    }, [messagesEndRef]);

    // Effect to scroll to bottom when messages list changes or active conversation changes
    useEffect(() => {
        if (messages.length > 0) {
            // Use a 300ms delay to ensure the DOM has rendered completely (especially for media items)
            const timer = setTimeout(() => {
                const behavior = lastViewedConversationId.current !== activeConversationId ? 'auto' : 'smooth';
                scrollToBottom(behavior);
                lastViewedConversationId.current = activeConversationId;
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [messages, activeConversationId, scrollToBottom]);

    const lastViewedConversationId = useRef(null);

    // Setup Reverb real-time listener (WebSocket — faster updates when available)
    useEffect(() => {
        if (!activeConversationId) return;

        const channelName = `conversations.${activeConversationId}`;
        
        let channel;
        try {
            channel = echo.private(channelName);
            
            // Listen for new messages
            channel.listen('.App\\Events\\MessageCreated', (data) => {
                console.log('Real-time Message Event Received:', data);
                // Immediately refetch when we get a real-time push
                queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
                queryClient.invalidateQueries({ queryKey: ['conversations'] });

                // Fix: if the message belongs to the currently active conversation and is inbound, 
                // mark it as read immediately to clear the UI badge
                if (data?.message?.direction === 'inbound') {
                    // Small delay to allow react to render the message first
                    setTimeout(() => {
                        markConversationAsRead(activeConversationId)
                            .then(() => {
                                clearConversationNotifications(activeConversationId);
                                queryClient.invalidateQueries({ queryKey: ['conversations'] });
                            })
                            .catch(err => console.error('Silent markAsRead failed', err));
                    }, 800);
                }
            });

            // Listen for deleted messages
            channel.listen('.App\\Events\\MessageDeleted', (data) => {
                console.log('Real-time Message Deleted Event Received:', data);
                queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
                queryClient.invalidateQueries({ queryKey: ['conversations'] });
            });

            // Listen for conversation read events (from other tabs/users)
            channel.listen('.App\\Events\\ConversationRead', (event) => {
                console.log('Real-time Read Event Received:', event);
                if (event.userId !== user?.id) {
                    // If someone else read it, update our list count
                    queryClient.invalidateQueries({ queryKey: ['conversations'] });
                }
                // Clear local header notifications if we are the ones who read it (cross-tab sync)
                if (event.userId === user?.id) {
                    clearConversationNotifications(activeConversationId);
                }
            });

        } catch (err) {
            console.warn('Reverb WebSocket unavailable, relying on polling only:', err.message);
        }

        return () => {
            if (channel) {
                try { echo.leave(channelName); } catch (_) {}
            }
        };
    }, [activeConversationId, queryClient, user?.id, clearConversationNotifications]);

    const sendMutation = useMutation({
        mutationFn: sendMessage,
        // Optimistic update — add the message to the UI immediately before API responds
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: ['messages', activeConversationId] });
            const previous = queryClient.getQueryData(['messages', activeConversationId]);
            
            queryClient.setQueryData(['messages', activeConversationId], (old) => {
                if (!old) return old;
                const parentMsg = variables.parent_message_id ? (old.data || []).find(m => m.id === variables.parent_message_id) : null;
                const optimisticMsg = {
                    id: `optimistic-${Date.now()}`,
                    body: variables.body,
                    direction: 'outbound',
                    type: 'text',
                    status: 'pending',
                    created_at: new Date().toISOString(),
                    conversation_id: variables.conversation_id,
                    metadata: parentMsg ? {
                        quoted_message_id: parentMsg.evolution_message_id,
                        quoted_message_body: parentMsg.body || (parentMsg.type === 'image' ? '📷 Foto' : parentMsg.type === 'video' ? '🎥 Video' : 'Archivo'),
                        quoted_message_type: parentMsg.type,
                        quoted_message_sender_phone: parentMsg.direction === 'outbound' ? null : (activeConversation?.contact?.phone || null),
                    } : null
                };
                return { ...old, data: [...(old.data || []), optimisticMsg] };
            });
            
            return { previous };
        },
        onSuccess: () => {
            // Force a fresh fetch to replace optimistic entry with real data
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
        },
        onError: (err, variables, context) => {
            // Rollback optimistic update on failure
            if (context?.previous) {
                queryClient.setQueryData(['messages', activeConversationId], context.previous);
            }
            console.error('Failed to send message:', err);
        },
    });

    // ── WhatsApp Catalog Query & Anti-Ban Handler ──
    const { 
        data: catalogResponse, 
        isLoading: catalogLoading, 
        refetch: refetchCatalog, 
        isFetching: catalogFetching 
    } = useQuery({
        queryKey: ['whatsappCatalog'],
        queryFn: () => getWhatsappCatalog(false),
        enabled: showCatalogDrawer && canUseCatalog,
        staleTime: 1000 * 60 * 15, // 15-minute anti-ban cache
    });

    const catalogProducts = Array.isArray(catalogResponse?.data) ? catalogResponse.data : [];
    const filteredCatalog = catalogProducts.filter(p => 
        (p.title || '').toLowerCase().includes(catalogQuery.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(catalogQuery.toLowerCase())
    );

    const handleSendProductFromCatalog = useCallback((product) => {
        if (!activeConversationId || sendingProductId) return;

        setSendingProductId(product.id);

        // 🤖 Anti-Ban: Simular presencia de escritura y retardo humano (800ms)
        sendTypingPresence(activeConversationId, 'composing');

        setTimeout(() => {
            sendMutation.mutate({
                conversation_id: activeConversationId,
                body: `🛍️ ${product.title}`,
                type: 'business',
                metadata: {
                    business_type: 'product',
                    product_id: product.id,
                    product_title: product.title,
                    product_desc: product.description,
                    product_price: product.price,
                    product_currency: product.currency || 'USD',
                    product_url: product.image_url,
                }
            }, {
                onSettled: () => {
                    setSendingProductId(null);
                }
            });
        }, 800);
    }, [activeConversationId, sendingProductId, sendMutation]);

    const refreshAvatarMutation = useMutation({
        mutationFn: refreshContactAvatar,
        onSuccess: (data) => {
            // Invalidate to show the new avatar once downloaded
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['conversations'] });
                queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
            }, 3000);
        },
    });

    const toggleIgnoreMutation = useMutation({
        mutationFn: async (id) => {
            const { default: axiosInstance } = await import('../lib/axios');
            return axiosInstance.post(`/api/leads/${id}/toggle-ignore`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
        }
    });

    const triggerOptimisticMedia = (file, type) => {
        const tempUrl = URL.createObjectURL(file);
        queryClient.setQueryData(['messages', activeConversationId], (old) => {
            if (!old) return old;
            const optimisticMsg = {
                id: `optimistic-${Date.now()}`,
                body: '',
                direction: 'outbound',
                type: type,
                media_url: tempUrl,
                status: 'pending',
                created_at: new Date().toISOString(),
                conversation_id: activeConversationId,
            };
            return { ...old, data: [...(old.data || []), optimisticMsg] };
        });
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    /**
     * Manda un sticker de la biblioteca.
     *
     * El backend puede negarse (429) cuando el freno anti-bloqueo lo detiene
     * —el contacto nunca escribió, o ese mismo sticker ya salió a demasiada
     * gente—. Ese motivo se muestra tal cual: es informacion que el vendedor
     * necesita para entender por qué no salió, no un error genérico.
     */
    const handlePickSticker = async (sticker) => {
        if (!activeConversationId || enviandoSticker) return;

        setEnviandoSticker(true);
        setStickerError(null);

        try {
            await sendStickerMessage(activeConversationId, sticker.id, replyingTo?.id ?? null);
            setShowStickers(false);
            setReplyingTo(null);
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
            queryClient.invalidateQueries({ queryKey: ['stickers'] });
        } catch (err) {
            setStickerError(err?.response?.data?.message || 'No se pudo enviar el sticker.');
        } finally {
            setEnviandoSticker(false);
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file || !activeConversationId) return;

        let type = 'document';
        if (file.type.startsWith('image/')) type = 'image';
        else if (file.type.startsWith('video/')) type = 'video';
        else if (file.type.startsWith('audio/')) type = 'audio';

        setIsUploadingMedia(true);
        triggerOptimisticMedia(file, type);
        
        try {
            await sendMediaMessage(activeConversationId, file, type, '', 0, replyingTo?.id || null);
            setReplyingTo(null);
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
        } catch (err) {
            console.error("Error uploading file", err);
            queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
        } finally {
            setIsUploadingMedia(false);
            if (fileInputRef.current) fileInputRef.current.value = null;
        }
    };

    const startRecording = async () => {
        if (!activeConversationId) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                stream.getTracks().forEach(track => track.stop());
                if (!isRecordingRef.current) return;
                
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/ogg; codecs=opus' });
                const audioFile = new File([audioBlob], "voice_note.ogg", { type: "audio/ogg" });
                
                setIsUploadingMedia(true);
                triggerOptimisticMedia(audioFile, 'voice_note');

                try {
                    await sendMediaMessage(activeConversationId, audioFile, 'voice_note', '', recordingTimerVal.current, replyingToRef.current?.id || null);
                    setReplyingTo(null);
                    queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
                    queryClient.invalidateQueries({ queryKey: ['conversations'] });
                } catch (err) {
                    console.error("Send VN error", err);
                    queryClient.invalidateQueries({ queryKey: ['messages', activeConversationId] });
                } finally {
                    setIsUploadingMedia(false);
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            isRecordingRef.current = true;
            recordingTimerVal.current = 0;

            recordingTimerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    recordingTimerVal.current = prev + 1;
                    return prev + 1;
                });
            }, 1000);
        } catch (error) {
            console.error("Microphone access denied:", error);
            alert("No se pudo acceder al micrófono.");
        }
    };

    const stopRecording = (cancel = false) => {
        if (!mediaRecorderRef.current || !isRecording) return;
        
        clearInterval(recordingTimerRef.current);
        if (cancel) isRecordingRef.current = false;
        
        mediaRecorderRef.current.stop();
        setIsRecording(false);
    };

    const handleSend = (e) => {
        e?.preventDefault();
        const messageBody = newMessage.trim();
        if (!messageBody || !activeConversationId || sendMutation.isPending) return;

        const parentMessageId = replyingTo?.id || null;
        setNewMessage(''); // Clear immediately for snappy feel
        setReplyingTo(null); // Clear reply context
        setShowSuggestions(false);

        sendMutation.mutate({
            conversation_id: activeConversationId,
            body: messageBody,
            type: 'text',
            parent_message_id: parentMessageId
        });
    };

    const handleSuggestionSelect = (template) => {
        if (!textareaRef.current) return;
        
        const text = newMessage;
        const cursorPosition = textareaRef.current.selectionStart;
        const textBeforeCursor = text.substring(0, cursorPosition);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');
        
        if (lastAtIndex === -1) return;

        let content = template.content;

        // Dynamic Variables Replacement
        if (activeConversation?.contact) {
            const contact = activeConversation.contact;
            content = content.replace(/{{nombre}}/g, contact.full_name || contact.first_name || '');
            content = content.replace(/{{empresa}}/g, contact.company || 'su empresa');
        }
        content = content.replace(/{{vendedor}}/g, user?.name || '');

        const newText = text.substring(0, lastAtIndex) + content + text.substring(cursorPosition);
        setNewMessage(newText);
        setShowSuggestions(false);
        
        // Refocus and set cursor (optional, might need a timeout)
        setTimeout(() => {
            textareaRef.current.focus();
            const newCursorPos = lastAtIndex + content.length;
            textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }, 10);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setNewMessage(value);
        handleTyping();

        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPosition);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const query = textBeforeCursor.substring(lastAtIndex + 1);
            // Solo activar si el @ está al inicio o precedido por un espacio
            const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
            
            if (charBeforeAt === ' ' || charBeforeAt === '\n') {
                setSuggestionQuery(query);
                const filtered = (allTemplates || []).filter(t => 
                    t.keyword.toLowerCase().includes(query.toLowerCase())
                );
                setFilteredTemplates(filtered);
                setSuggestionIndex(0);
                setShowSuggestions(filtered.length > 0);
            } else {
                setShowSuggestions(false);
            }
        } else {
            setShowSuggestions(false);
        }
    };

    // Handle click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyPress = (e) => {
        if (showSuggestions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSuggestionIndex(prev => (prev + 1) % filteredTemplates.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSuggestionIndex(prev => (prev - 1 + filteredTemplates.length) % filteredTemplates.length);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                handleSuggestionSelect(filteredTemplates[suggestionIndex]);
            } else if (e.key === 'Escape') {
                setShowSuggestions(false);
            }
            return;
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="flex flex-col h-full w-full max-w-full bg-slate-50 dark:bg-background-dark overflow-hidden">
            <div className={`${activeConversationId ? 'hidden md:block' : 'block'} w-full`}>
                <Header title="Mensajes" />
            </div>
            
            <div className="flex flex-1 w-full max-w-full overflow-hidden m-0 md:m-6 rounded-none md:rounded-2xl border-none md:border border-gray-200 dark:border-gray-800 bg-white dark:bg-surface-dark shadow-none md:shadow-sm box-border">
                
                {/* Left Panel: Conversations List */}
                <div className={`${activeConversationId ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 flex-col border-r border-gray-100 dark:border-gray-800 shrink-0`}>
                    <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex flex-col gap-3 shrink-0">
                        <div className="relative">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">search</span>
                            <input 
                                type="text" 
                                placeholder="Buscar chat..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-none rounded-xl text-sm focus:ring-1 focus:ring-primary/50 text-text-main dark:text-gray-200"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button 
                                onClick={() => setFilterType('all')}
                                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${filterType === 'all' ? 'bg-primary/10 text-primary' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                            >
                                Todos
                            </button>
                            <button 
                                onClick={() => setFilterType('unread')}
                                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${filterType === 'unread' ? 'bg-primary/10 text-primary' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                            >
                                No leídos
                            </button>
                        </div>
                    </div>
                    
                    <div 
                        className="flex-1 overflow-y-auto no-scrollbar"
                        onScroll={handleScroll}
                    >
                        {convsLoading && conversations.length === 0 ? (
                            <div className="p-8 text-center text-text-secondary">Cargando chats...</div>
                        ) : !Array.isArray(conversations) || conversations.length === 0 ? (
                            <div className="p-8 text-center text-text-secondary flex flex-col items-center gap-3">
                                <span className="material-symbols-outlined text-4xl opacity-20">chat_bubble</span>
                                <p className="text-sm">No tienes conversaciones activas.</p>
                            </div>
                        ) : (
                            (conversations || []).map((conv) => (
                                <button
                                    key={conv.id}
                                    onClick={() => setActiveConversationId(conv.id)}
                                    onMouseEnter={(e) => {
                                        setHoveredConvId(conv.id);
                                        setHoveredRect(e.currentTarget.getBoundingClientRect());
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredConvId(null);
                                        setHoveredRect(null);
                                    }}
                                    className={`w-full p-4 flex gap-3 text-left transition-colors border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/50
                                        ${activeConversationId === conv.id ? 'bg-primary/5 dark:bg-primary/10 relative before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-primary' : ''}
                                    `}
                                >
                                    <div className="relative shrink-0">
                                        {conv.contact?.avatar ? (
                                            <div className="w-12 h-12 rounded-full bg-cover bg-center" style={{ backgroundImage: `url(${resolveMediaUrl(conv.contact.avatar)})` }}></div>
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                                                {(conv.contact?.full_name || conv.contact?.first_name || 'C')?.substring(0, 2).toUpperCase()}
                                            </div>
                                        )}
                                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white dark:border-surface-dark flex items-center justify-center">
                                            <span className="material-symbols-outlined text-[10px] text-white">whatsapp</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-0.5">
                                            <span className={`font-bold text-sm truncate pr-2 ${conv.unread_count > 0 ? 'text-text-main dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {conv.contact?.full_name || conv.contact?.first_name || formatJidToPhone(conv.remote_jid)}
                                            </span>
                                            <span className={`text-[11px] shrink-0 pt-0.5 ${conv.unread_count > 0 ? 'text-primary font-bold' : 'text-gray-400'}`}>
                                                {conv.last_message_at ? dayjs(conv.last_message_at).format('HH:mm') : ''}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <p className={`text-[13px] truncate pr-2 ${conv.unread_count > 0 ? 'text-text-main dark:text-gray-300 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                                                {conv.latest_message?.body || 'Nuevo chat iniciado...'}
                                            </p>
                                            {conv.unread_count > 0 && (
                                                <span className="bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                                                    {conv.unread_count}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Panel: Chat Area */}
                {activeConversationId ? (
                    <div className="flex flex-col flex-1 h-full w-full max-w-full bg-[#EFEAE2] dark:bg-[#111b21] relative before:absolute before:inset-0 before:bg-[url('/wa-bg.png')] before:opacity-40 before:pointer-events-none before:dark:opacity-[0.05] box-border">
                        
                        {/* Chat Header */}
                        <div className="h-16 bg-gray-100 dark:bg-[#202c33] px-3 md:px-4 flex items-center justify-between z-30 shrink-0 border-l border-gray-200 dark:border-gray-800">
                            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                                {/* Botón volver para móvil */}
                                <button 
                                    onClick={() => setActiveConversationId(null)}
                                    className="md:hidden p-1.5 -ml-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors shrink-0 flex items-center justify-center"
                                >
                                    <span className="material-symbols-outlined text-[24px]">arrow_back</span>
                                </button>
                                
                                {activeConversation?.contact?.avatar ? (
                                    <div className="w-10 h-10 rounded-full bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${resolveMediaUrl(activeConversation?.contact?.avatar)})` }}></div>
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center font-bold text-slate-600 dark:text-slate-300 shrink-0 text-sm">
                                        {activeConversation?.contact?.full_name?.substring(0, 2).toUpperCase() || 'CA'}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1 pr-2">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-text-main dark:text-white text-sm truncate">{activeConversation?.contact?.full_name || activeConversation?.contact?.first_name || formatJidToPhone(activeConversation?.remote_jid)}</h3>
                                        {activeConversation?.contact?.is_ignored && (
                                            <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-600">
                                                No Lead
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 truncate">{formatJidToPhone(activeConversation?.remote_jid) || 'WhatsApp'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 md:gap-4 text-gray-500 dark:text-gray-400 shrink-0">
                                <button 
                                    onClick={() => refreshAvatarMutation.mutate(activeConversation?.contact?.id || activeConversation?.contact_id)}
                                    className={`hover:text-primary transition ${refreshAvatarMutation.isPending ? 'animate-spin text-primary' : ''}`}
                                    title="Actualizar foto de WhatsApp"
                                    disabled={refreshAvatarMutation.isPending || (!activeConversation?.contact?.id && !activeConversation?.contact_id)}
                                >
                                    <span className="material-symbols-outlined text-[20px]">sync</span>
                                </button>
                                <button className="hover:text-gray-700 dark:hover:text-gray-200 transition"><span className="material-symbols-outlined text-[20px]">search</span></button>
                                
                                <div className="relative" ref={menuRef}>
                                    <button 
                                        onClick={() => setShowMenu(!showMenu)}
                                        className={`hover:text-gray-700 dark:hover:text-gray-200 transition flex items-center justify-center p-1 rounded-full ${showMenu ? 'bg-gray-200 dark:bg-gray-700 text-primary' : ''}`}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">more_vert</span>
                                    </button>

                                    {showMenu && (
                                        <div 
                                            className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#232d36] rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-[100] overflow-hidden"
                                            style={{ animation: 'dropdownIn 0.2s ease-out forwards' }}
                                        >
                                            <div className="py-2">
                                                <button 
                                                    onClick={() => {
                                                        setShowMenu(false);
                                                        const targetContactId = activeConversation?.contact?.id || activeConversation?.contact_id;
                                                        if (targetContactId) {
                                                            navigate(`/contacts/${targetContactId}/360`);
                                                        } else {
                                                            alert('Esta conversación no tiene un contacto vinculado.');
                                                        }
                                                    }}
                                                    disabled={!activeConversation || (!activeConversation?.contact?.id && !activeConversation?.contact_id)}
                                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                                        (!activeConversation?.contact?.id && !activeConversation?.contact_id)
                                                            ? 'opacity-40 cursor-not-allowed text-gray-400' 
                                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                                                    }`}
                                                >
                                                    <span className="material-symbols-outlined text-[20px] text-gray-400">360</span>
                                                    <span className="font-medium">Vista 360</span>
                                                </button>
                                                
                                                <button 
                                                    onClick={() => {
                                                        setShowMenu(false);
                                                        setIsContactModalOpen(true);
                                                    }}
                                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-t border-gray-50 dark:border-gray-800"
                                                >
                                                    <span className="material-symbols-outlined text-[20px] text-gray-400">edit</span>
                                                    <span className="font-medium">Editar contacto</span>
                                                </button>

                                                <button 
                                                    onClick={() => {
                                                        setShowMenu(false);
                                                        const targetContactId = activeConversation?.contact?.id || activeConversation?.contact_id;
                                                        if (targetContactId) {
                                                            toggleIgnoreMutation.mutate(targetContactId);
                                                        }
                                                    }}
                                                    disabled={!activeConversation?.contact?.id && !activeConversation?.contact_id}
                                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors border-t border-gray-50 dark:border-gray-800 ${
                                                        (!activeConversation?.contact?.id && !activeConversation?.contact_id)
                                                            ? 'opacity-40 cursor-not-allowed text-gray-400'
                                                            : activeConversation?.contact?.is_ignored 
                                                                ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20' 
                                                                : 'text-slate-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                                                    }`}
                                                >
                                                    <span className={`material-symbols-outlined text-[20px] ${activeConversation?.contact?.is_ignored ? 'text-green-500' : 'text-gray-400'}`}>
                                                        {activeConversation?.contact?.is_ignored ? 'person_add' : 'person_off'}
                                                    </span>
                                                    <span className="font-medium">
                                                        {activeConversation?.contact?.is_ignored ? 'Desmarcar como No Lead' : 'Ignorar como No Lead'}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-3 md:p-4 flex flex-col gap-2 z-0 overflow-x-hidden">
                            {msgsLoading && (!Array.isArray(messages) || messages.length === 0) ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-primary animate-spin">sync</span>
                                </div>
                            ) : !Array.isArray(messages) || messages.length === 0 ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur text-gray-500 text-xs px-4 py-2 rounded-lg text-center max-w-xs leading-relaxed shadow-sm">
                                        Este es el inicio de tu conversación con {activeConversation?.contact?.full_name}.
                                    </div>
                                </div>
                            ) : (
                                (() => {
                                    let lastDate = null;
                                    return (messages || []).map((msg, index) => {
                                        const currentDate = dayjs(msg.created_at).format('YYYY-MM-DD');
                                        const showDateSeparator = currentDate !== lastDate;
                                        lastDate = currentDate;

                                        const isOutbound = String(msg.direction).toLowerCase() === 'outbound' || 
                                                         msg.is_ai_generated === true || 
                                                         String(msg.is_ai_generated) === '1';
                                        
                                        const isAi = msg.is_ai_generated === true || String(msg.is_ai_generated) === '1';
                                        
                                        const nextMsg = messages[index + 1];
                                        const isNextOutbound = nextMsg ? (
                                            String(nextMsg.direction).toLowerCase() === 'outbound' || 
                                            nextMsg.is_ai_generated === true || 
                                            String(nextMsg.is_ai_generated) === '1'
                                        ) : null;
                                        
                                        const showTail = index === messages.length - 1 || isNextOutbound !== isOutbound;
                                        
                                        const isOptimistic = String(msg.id).startsWith('optimistic-');
                                        // Retenido por el módulo Tutor: la IA lo redactó pero NUNCA se
                                        // envió, espera que alguien lo apruebe. Sin distinguirlo se
                                        // pintaba con el mismo tilde gris que un mensaje entregado y sin
                                        // leer, y parecía que el cliente no lo había abierto.
                                        const isPendingApproval = msg.is_pending_tutor_approval === true ||
                                                                  String(msg.is_pending_tutor_approval) === '1';
                                        const currentReaction = msg.reaction || selectedReactions[msg.id];
                                        const isHovered = hoveredMessageId === msg.id;
                                        
                                        return (
                                            <div key={msg.id} id={`msg-${msg.id}`} className="flex flex-col w-full">
                                                {showDateSeparator && (
                                                    <div className="flex justify-center my-4 sticky top-2 z-10">
                                                        <div className="bg-white/90 dark:bg-[#202c33]/90 backdrop-blur px-3 py-1.5 rounded-lg text-[11px] font-bold text-gray-500 dark:text-gray-400 shadow-sm uppercase tracking-wider border border-gray-100 dark:border-gray-700/50">
                                                            {formatChatDate(msg.created_at)}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                <div
                                                    className={`flex w-full ${isOutbound ? 'justify-end' : 'justify-start'} group/msg`}
                                                    onMouseEnter={() => !isOutbound && !isOptimistic && !msg.is_ai_generated && handleMouseEnter(msg.id)}
                                                    onMouseLeave={handleMouseLeave}
                                                >
                                                    <div className={`relative flex flex-col ${isOutbound ? 'items-end' : 'items-start'}`}>
                                                         {/* Floating action buttons (Reply & Delete) */}
                                                         {!isOptimistic && (
                                                             <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-1 z-10 ${isOutbound ? '-left-20' : '-right-20'}`}>
                                                                 <button
                                                                     onClick={() => setReplyingTo(msg)}
                                                                     className="p-1.5 rounded-full bg-white dark:bg-[#2a3942] text-gray-500 hover:text-primary dark:text-gray-300 dark:hover:text-primary shadow-md border border-gray-100 dark:border-gray-700 transition"
                                                                     title="Responder"
                                                                 >
                                                                     <span className="material-symbols-outlined text-[16px] leading-none block">reply</span>
                                                                 </button>
                                                                 {canDeleteMessages && (
                                                                 <button
                                                                     onClick={() => setDeletingMessage(msg)}
                                                                     className="p-1.5 rounded-full bg-white dark:bg-[#2a3942] text-gray-500 hover:text-red-500 dark:text-gray-300 dark:hover:text-red-400 shadow-md border border-gray-100 dark:border-gray-700 transition"
                                                                     title="Eliminar mensaje"
                                                                 >
                                                                     <span className="material-symbols-outlined text-[16px] leading-none block">delete</span>
                                                                 </button>
                                                                 )}
                                                             </div>
                                                         )}

                                                        {/* Emoji Picker — aparece al hover sobre mensajes inbound */}
                                                        {canReact && !isOutbound && !isOptimistic && isHovered && (
                                                            <div
                                                                className="absolute -top-9 left-0 z-30 flex items-center gap-1 bg-white dark:bg-[#2a3942] rounded-full shadow-lg px-2 py-1 border border-gray-100 dark:border-gray-700"
                                                                style={{
                                                                    animation: 'reactionPickerIn 0.15s ease-out forwards',
                                                                }}
                                                                onMouseEnter={() => handleMouseEnter(msg.id)}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                {REACTION_EMOJIS.map(emoji => (
                                                                    <button
                                                                        key={emoji}
                                                                        onClick={() => handleReaction(msg, emoji)}
                                                                        className="text-[18px] hover:scale-125 transition-transform duration-100 leading-none p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                                                        title={emoji}
                                                                    >
                                                                        {emoji}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Burbuja del mensaje */}
                                                        <div className={`
                                                            relative max-w-[85%] sm:max-w-[75%] px-3 py-2 text-[14px] leading-relaxed shadow-sm w-fit min-w-[80px]
                                                            ${msg.type === 'sticker' ? 'bg-transparent shadow-none px-0 py-0' :
                                                              isOutbound 
                                                                ? (isAi 
                                                                    ? 'bg-orange-50 text-[#111b21] dark:bg-[#3d2e1f] dark:text-[#e9edef] border border-orange-200 dark:border-orange-800/50 rounded-lg' 
                                                                    : 'bg-[#d9fdd3] text-[#111b21] dark:bg-[#005c4b] dark:text-[#e9edef] rounded-lg')
                                                                : 'bg-white text-[#111b21] dark:bg-[#202c33] dark:text-[#e9edef] rounded-lg'
                                                            }
                                                            ${showTail && isOutbound && msg.type !== 'sticker' ? 'rounded-br-none' : ''}
                                                            ${showTail && !isOutbound && msg.type !== 'sticker' ? 'rounded-bl-none' : ''}
                                                            ${isOptimistic ? 'opacity-70' : ''}
                                                        `}>
                                                            <div className="flex flex-col">
                                                                {/* Forwarded Status Label */}
                                                                {msg.metadata?.is_forwarded && (
                                                                    <div className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 mb-1 italic">
                                                                        <span className="material-symbols-outlined text-[13px] scale-x-[-1] leading-none">forward</span>
                                                                        <span>Reenviado</span>
                                                                    </div>
                                                                )}

                                                                {/* Quoted Message Preview Box */}
                                                                {msg.metadata?.quoted_message_id && (
                                                                    <div 
                                                                        onClick={() => {
                                                                            const targetMsg = (messages || []).find(m => m.evolution_message_id === msg.metadata.quoted_message_id);
                                                                            if (targetMsg) {
                                                                                scrollToMessage(targetMsg.id);
                                                                            }
                                                                        }}
                                                                        className="mb-1.5 rounded border-l-[3px] border-primary/70 bg-black/5 dark:bg-black/20 p-2 cursor-pointer hover:bg-black/10 dark:hover:bg-black/30 transition-colors text-left"
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            {/* Ficha de catálogo citada: sin la miniatura no se
                                                                                entiende de qué producto habla el cliente. */}
                                                                            {msg.metadata.quoted_product_thumbnail_url && (
                                                                                <img
                                                                                    src={resolveMediaUrl(msg.metadata.quoted_product_thumbnail_url)}
                                                                                    alt=""
                                                                                    loading="lazy"
                                                                                    className="w-11 h-11 rounded object-cover shrink-0 bg-black/10"
                                                                                />
                                                                            )}
                                                                            <div className="flex flex-col leading-snug min-w-0">
                                                                                <span className="font-bold text-primary text-[11px] truncate">
                                                                                    {msg.metadata.quoted_message_sender_phone ? `+${msg.metadata.quoted_message_sender_phone}` : 'Tú'}
                                                                                </span>
                                                                                <span className="text-gray-600 dark:text-gray-300 truncate max-w-[240px] mt-0.5 opacity-90 italic text-[12px]">
                                                                                    {msg.metadata.quoted_message_body || 'Mensaje citado'}
                                                                                </span>
                                                                                {/* Una ficha con varias fotos es una promo de varios
                                                                                    modelos: WhatsApp no dice cuál miraba el cliente,
                                                                                    y el vendedor tiene que saberlo de un vistazo. */}
                                                                                {msg.metadata.quoted_message_type === 'product' && (msg.metadata.quoted_product_image_count > 1 || msg.metadata.quoted_product_description) && (
                                                                                    <span className="text-gray-500 dark:text-gray-400 truncate max-w-[240px] text-[11px]">
                                                                                        {msg.metadata.quoted_product_image_count > 1 && (
                                                                                            <span className="font-semibold text-amber-600 dark:text-amber-400">
                                                                                                {msg.metadata.quoted_product_image_count} fotos · modelo no identificado
                                                                                            </span>
                                                                                        )}
                                                                                        {msg.metadata.quoted_product_image_count > 1 && msg.metadata.quoted_product_description && ' · '}
                                                                                        {msg.metadata.quoted_product_description}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {/* ── STATUS REPLY CARD ── */}
                                                                {msg.metadata?.is_status_reply && (
                                                                    <div className="mb-1.5 rounded-xl overflow-hidden border-l-[3px] border-purple-500 shadow-sm bg-black/5 dark:bg-black/20">
                                                                        {msg.metadata?.quoted_status_thumbnail_url ? (
                                                                            <div className="relative">
                                                                                <img
                                                                                    src={resolveMediaUrl(msg.metadata.quoted_status_thumbnail_url)}
                                                                                    alt="Estado citado"
                                                                                    className="w-full max-h-28 object-cover opacity-80"
                                                                                />
                                                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-1.5">
                                                                                    <div className="flex items-center gap-1">
                                                                                        <span className="material-symbols-outlined text-white text-[13px]">motion_photos_on</span>
                                                                                        <span className="text-white text-[11px] font-bold uppercase tracking-wide">Estado</span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex items-center gap-2 px-2 py-1.5">
                                                                                <span className="material-symbols-outlined text-purple-500 text-base flex-shrink-0">motion_photos_on</span>
                                                                                <div className="flex flex-col overflow-hidden leading-tight">
                                                                                    <span className="font-bold text-purple-600 dark:text-purple-400 text-[11px] uppercase tracking-wide">Estado</span>
                                                                                    <span className="text-gray-600 dark:text-gray-300 truncate max-w-[200px] mt-0.5 opacity-90 italic text-xs">
                                                                                        {msg.metadata?.quoted_status_content || 'Estado no disponible'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {msg.metadata?.quoted_status_thumbnail_url && msg.metadata?.quoted_status_content && (
                                                                            <div className="px-2 py-1 text-[11px] text-gray-600 dark:text-gray-300 italic truncate">
                                                                                {msg.metadata.quoted_status_content}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* ── LOCATION CARD ── */}
                                                                {msg.type === 'location' && msg.metadata?.latitude && (
                                                                    <a
                                                                        href={`https://www.google.com/maps?q=${msg.metadata.latitude},${msg.metadata.longitude}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="block mb-1.5 rounded-xl overflow-hidden border border-black/10 dark:border-white/10 hover:opacity-90 transition-opacity group/loccard"
                                                                    >
                                                                        {/* Static map preview via Google Maps Embed */}
                                                                        <div className="relative w-full h-28 bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                                                            <img
                                                                                src={`https://maps.googleapis.com/maps/api/staticmap?center=${msg.metadata.latitude},${msg.metadata.longitude}&zoom=15&size=400x200&markers=${msg.metadata.latitude},${msg.metadata.longitude}&key=AIzaSyD-PLACEHOLDER`}
                                                                                alt="Mapa"
                                                                                className="w-full h-full object-cover"
                                                                                onError={e => {
                                                                                    // Fallback: plain pin icon on colored bg if no API key
                                                                                    e.target.style.display = 'none';
                                                                                }}
                                                                            />
                                                                            {/* Always-visible pin overlay */}
                                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                                <div className="w-10 h-10 rounded-full bg-red-500 shadow-lg flex items-center justify-center ring-4 ring-white/60">
                                                                                    <span className="material-symbols-outlined text-white text-[22px]">location_on</span>
                                                                                </div>
                                                                            </div>
                                                                            <div className="absolute top-2 right-2 bg-white dark:bg-gray-800 rounded-full p-1 shadow-md opacity-0 group-hover/loccard:opacity-100 transition-opacity">
                                                                                <span className="material-symbols-outlined text-primary text-[14px] block">open_in_new</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="bg-white/80 dark:bg-black/30 px-2.5 py-1.5">
                                                                            {msg.metadata.place_name && (
                                                                                <p className="text-[12px] font-semibold text-gray-800 dark:text-gray-100 truncate">{msg.metadata.place_name}</p>
                                                                            )}
                                                                            {msg.metadata.address && (
                                                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">{msg.metadata.address}</p>
                                                                            )}
                                                                            <p className="text-[10px] text-primary font-medium mt-0.5 flex items-center gap-0.5">
                                                                                <span className="material-symbols-outlined text-[11px]">near_me</span>
                                                                                Abrir en Maps
                                                                            </p>
                                                                        </div>
                                                                    </a>
                                                                )}

                                                                {/* ── CONTACT CARD ── */}
                                                                {msg.type === 'contact' && (
                                                                    <div className="mb-1.5 rounded-xl border border-black/10 dark:border-white/10 overflow-hidden bg-white/60 dark:bg-white/5">
                                                                        <div className="flex items-center gap-3 px-3 py-2.5">
                                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                                                                                <span className="material-symbols-outlined text-white text-[22px]">person</span>
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <p className="font-semibold text-[13px] text-gray-800 dark:text-gray-100 truncate">
                                                                                    {msg.metadata?.contact_name || 'Contacto'}
                                                                                </p>
                                                                                {msg.metadata?.contact_phone && (
                                                                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                                                        +{msg.metadata.contact_phone}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        {msg.metadata?.contact_phone && (
                                                                            <a
                                                                                href={`https://wa.me/${msg.metadata.contact_phone}`}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="flex items-center justify-center gap-1.5 py-1.5 border-t border-black/5 dark:border-white/5 text-[12px] text-green-600 dark:text-green-400 font-medium hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[14px]">whatsapp</span>
                                                                                Enviar WhatsApp
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* ── BUSINESS / CATALOG CARD ── */}
                                                                {msg.type === 'business' && (() => {
                                                                    const btype = msg.metadata?.business_type;
                                                                    if (btype === 'product') {
                                                                        const rawPrice = msg.metadata?.product_price;
                                                                        const numPrice = typeof rawPrice === 'number'
                                                                            ? (rawPrice > 1000 ? rawPrice / 1000 : rawPrice)
                                                                            : parseFloat(rawPrice || 0);

                                                                        return (
                                                                            <div className="mb-1.5 rounded-xl border border-emerald-500/25 dark:border-emerald-500/35 overflow-hidden bg-gradient-to-b from-white to-emerald-50/20 dark:from-[#1f2c34] dark:to-[#111b21] shadow-xs max-w-[280px] sm:max-w-[310px]">
                                                                                {/* Header Bar */}
                                                                                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 py-1.5 flex items-center justify-between shadow-inner">
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <span className="material-symbols-outlined text-[15px]">storefront</span>
                                                                                        <span className="text-[10.5px] font-bold tracking-wide uppercase">Catálogo WhatsApp</span>
                                                                                    </div>
                                                                                    <span className="text-[9.5px] bg-white/20 px-1.5 py-0.5 rounded-full font-medium backdrop-blur-xs">Producto</span>
                                                                                </div>

                                                                                {/* Product Image or Branded Placeholder */}
                                                                                {msg.metadata?.product_url ? (
                                                                                    <div className="relative w-full h-36 sm:h-40 bg-gray-100 dark:bg-gray-800 overflow-hidden group/prodimg">
                                                                                        <img 
                                                                                            src={resolveMediaUrl(msg.metadata.product_url)} 
                                                                                            alt={msg.metadata.product_title} 
                                                                                            className="w-full h-full object-cover group-hover/prodimg:scale-105 transition-transform duration-300 cursor-pointer"
                                                                                            onClick={() => window.open(resolveMediaUrl(msg.metadata.product_url), '_blank')}
                                                                                            onError={e => {
                                                                                                if (e.target.parentElement) e.target.parentElement.style.display = 'none';
                                                                                            }} 
                                                                                        />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="w-full h-20 bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-emerald-500/5 dark:from-emerald-950/40 dark:to-teal-900/30 flex items-center justify-center border-b border-emerald-500/10">
                                                                                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 dark:bg-emerald-500/30 flex items-center justify-center">
                                                                                            <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-[22px]">shopping_bag</span>
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* Details Container */}
                                                                                <div className="p-3 space-y-2">
                                                                                    <div className="flex items-start justify-between gap-2">
                                                                                        <h4 className="font-bold text-[13.5px] text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 flex-1">
                                                                                            {msg.metadata?.product_title || 'Producto del catálogo'}
                                                                                        </h4>
                                                                                        {/* Price Badge */}
                                                                                        {rawPrice != null && rawPrice !== '' && (
                                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-extrabold text-[12px] whitespace-nowrap shadow-2xs">
                                                                                                {msg.metadata?.product_currency || '$'} {numPrice > 0 ? numPrice.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>

                                                                                    {msg.metadata?.product_desc && (
                                                                                        <p className="text-[11.5px] text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-3 bg-black/5 dark:bg-white/5 p-2 rounded-lg italic">
                                                                                            {msg.metadata.product_desc}
                                                                                        </p>
                                                                                    )}

                                                                                    {/* Interactive Action Button */}
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            if (msg.metadata?.product_title) {
                                                                                                setNewMessage(`Me interesa este producto: ${msg.metadata.product_title}`);
                                                                                            }
                                                                                        }}
                                                                                        className="w-full mt-2 py-1.5 px-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98]"
                                                                                    >
                                                                                        <span className="material-symbols-outlined text-[15px]">chat</span>
                                                                                        Consultar sobre este producto
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    if (btype === 'order') return (
                                                                        <div className="mb-1.5 rounded-xl border border-black/10 dark:border-white/10 overflow-hidden bg-white/60 dark:bg-white/5 px-3 py-2.5">
                                                                            <div className="flex items-center gap-1.5 mb-1.5">
                                                                                <span className="material-symbols-outlined text-[18px] text-orange-500">shopping_cart</span>
                                                                                <span className="text-[11px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wide">Pedido recibido</span>
                                                                            </div>
                                                                            <div className="text-[12px] text-gray-600 dark:text-gray-300 space-y-0.5">
                                                                                {msg.metadata.order_id && <p><span className="font-medium">ID:</span> {msg.metadata.order_id}</p>}
                                                                                {msg.metadata.item_count > 0 && <p><span className="font-medium">Artículos:</span> {msg.metadata.item_count}</p>}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                    if (btype === 'list_response' || btype === 'buttons_response' || btype === 'template_reply') return (
                                                                        <div className="mb-1.5 rounded-xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-white/5 px-3 py-2 flex items-center gap-2">
                                                                            <span className="material-symbols-outlined text-[18px] text-blue-500 flex-shrink-0">
                                                                                {btype === 'list_response' ? 'checklist' : 'touch_app'}
                                                                            </span>
                                                                            <div>
                                                                                <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                                                                                    {btype === 'list_response' ? 'Opción seleccionada de lista' : 'Botón seleccionado'}
                                                                                </p>
                                                                                <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">{msg.metadata.selected_title || msg.metadata.selected_id}</p>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                    return null;
                                                                })()}
                                                                {msg.type === 'image' && msg.media_url && (
                                                                    <div className="mb-1 -mx-1 -mt-1 rounded overflow-hidden">
                                                                        <img 
                                                                            src={resolveMediaUrl(msg.media_url)} 
                                                                            alt="Adjunto" 
                                                                            className="max-w-xs max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                                                                            onClick={() => window.open(resolveMediaUrl(msg.media_url), '_blank')} 
                                                                        />
                                                                    </div>
                                                                )}
                                                                {msg.type === 'sticker' && msg.media_url && (
                                                                    <div className="mb-0 drop-shadow-md">
                                                                        <img src={resolveMediaUrl(msg.media_url)} alt="Sticker" className="w-32 h-32 object-contain" />
                                                                    </div>
                                                                )}
                                                                {msg.type === 'video' && (
                                                                    <div className="mb-1 -mx-1 -mt-1 rounded overflow-hidden">
                                                                        {msg.media_url ? (
                                                                            /* ── Already downloaded: show the player ── */
                                                                            <div className="relative">
                                                                                <video
                                                                                    src={resolveMediaUrl(msg.media_url)}
                                                                                    controls
                                                                                    className="max-w-xs max-h-64 w-full block"
                                                                                />
                                                                                {/* Download to device button */}
                                                                                <a
                                                                                    href={resolveMediaUrl(msg.media_url)}
                                                                                    download
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                    className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
                                                                                    title="Descargar video"
                                                                                    onClick={e => e.stopPropagation()}
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px] leading-none block">download</span>
                                                                                </a>
                                                                            </div>
                                                                        ) : (
                                                                            /* ── Not yet downloaded: lazy-load card ── */
                                                                            <div
                                                                                className="relative w-64 h-36 bg-gray-900 dark:bg-black/60 rounded overflow-hidden cursor-pointer group/vidcard"
                                                                                onClick={() => handleVideoDownload(msg)}
                                                                                title="Toca para descargar y reproducir el video"
                                                                            >
                                                                                {/* Thumbnail or gradient placeholder */}
                                                                                {msg.metadata?.thumbnail_url ? (
                                                                                    <img
                                                                                        src={resolveMediaUrl(msg.metadata.thumbnail_url)}
                                                                                        alt="Vista previa del video"
                                                                                        className="w-full h-full object-cover opacity-70 group-hover/vidcard:opacity-80 transition-opacity"
                                                                                    />
                                                                                ) : (
                                                                                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 dark:from-gray-800 dark:to-black" />
                                                                                )}

                                                                                {/* Centered play / spinner overlay */}
                                                                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                                                                                    {downloadingVideoIds.has(msg.id) ? (
                                                                                        <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center">
                                                                                            <span className="material-symbols-outlined text-white text-[28px] animate-spin">sync</span>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors shadow-lg group-hover/vidcard:scale-110 transition-transform">
                                                                                            <span className="material-symbols-outlined text-white text-[32px] leading-none ml-0.5">play_arrow</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {!downloadingVideoIds.has(msg.id) && (
                                                                                        <div className="flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5">
                                                                                            <span className="material-symbols-outlined text-white text-[12px] leading-none">download</span>
                                                                                            <span className="text-white text-[11px] font-medium">Toca para ver</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>

                                                                                {/* Duration badge (bottom-left) */}
                                                                                {msg.metadata?.duration_secs > 0 && (
                                                                                    <div className="absolute bottom-1.5 left-2 bg-black/60 rounded px-1.5 py-0.5 text-white text-[10px] font-mono">
                                                                                        {String(Math.floor(msg.metadata.duration_secs / 60)).padStart(2, '0')}:{String(msg.metadata.duration_secs % 60).padStart(2, '0')}
                                                                                    </div>
                                                                                )}

                                                                                {/* Video icon badge (top-left) */}
                                                                                <div className="absolute top-1.5 left-1.5 bg-black/50 rounded p-0.5">
                                                                                    <span className="material-symbols-outlined text-white text-[14px] leading-none block">videocam</span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {(msg.type === 'audio' || msg.type === 'voice_note') && msg.media_url && (
                                                                    <div className="mb-1 w-full min-w-[280px] sm:min-w-[320px] py-1">
                                                                        <audio 
                                                                            src={resolveMediaUrl(msg.media_url)} 
                                                                            controls 
                                                                            className="w-full h-10 filter dark:invert dark:hue-rotate-180 opacity-90 hover:opacity-100 transition-opacity" 
                                                                        />
                                                                    </div>
                                                                )}
                                                                {msg.type === 'document' && msg.media_url && (
                                                                    <div className="mb-1 bg-black/5 dark:bg-white/5 p-2 rounded flex items-center gap-2">
                                                                        <div className="w-10 h-10 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
                                                                            <span className="material-symbols-outlined">description</span>
                                                                        </div>
                                                                        <a href={resolveMediaUrl(msg.media_url)} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline truncate inline-block max-w-[200px]" title="Ver/Descargar documento">Documento Adjunto</a>
                                                                    </div>
                                                                )}
                                                                
                                                                <div className="relative min-w-[80px]">
                                                                    {(() => {
                                                                        const isRedundantProductBody = msg.type === 'business' && 
                                                                            msg.metadata?.business_type === 'product' && 
                                                                            msg.body && 
                                                                            (
                                                                                msg.body.trim() === `🛍️ ${msg.metadata?.product_title || ''}`.trim() ||
                                                                                msg.body.trim() === (msg.metadata?.product_title || '').trim() ||
                                                                                msg.body.trim() === '🛍️ Producto'
                                                                            );

                                                                        if (!isRedundantProductBody && msg.body) {
                                                                            return (
                                                                                <div className="text-[14px] break-words whitespace-pre-wrap">
                                                                                    {msg.body}
                                                                                    <div className={`inline-flex items-center gap-1 opacity-70 ml-2 align-bottom h-[14px] leading-none mb-[-1px] ${msg.type === 'sticker' ? 'bg-black/40 backdrop-blur-md px-1.5 py-1 rounded-full text-white' : ''}`}>
                                                                                        {isAi && (
                                                                                            <span className="text-[9px] font-bold bg-orange-500/20 text-orange-700 dark:text-orange-300 px-1 rounded mr-0.5">IA</span>
                                                                                        )}
                                                                                        <span className="text-[10px] whitespace-nowrap">
                                                                                            {dayjs(msg.created_at).format('HH:mm')}
                                                                                        </span>
                                                                                        {isOutbound && isPendingApproval && (
                                                                                            <span className="text-[9px] font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 px-1 rounded whitespace-nowrap" title="Redactado por la IA, sin enviar: espera aprobación en Tutor">
                                                                                                sin enviar
                                                                                            </span>
                                                                                        )}
                                                                                        {isOutbound && !isPendingApproval && (
                                                                                            <span className={`material-symbols-outlined text-[14px] leading-none block ${msg.status === 'read' ? 'text-blue-500' : ''}`}>
                                                                                                {isOptimistic ? 'schedule' : (msg.status === 'read' || msg.status === 'delivered') ? 'done_all' : 'check'}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <div className={`flex items-center justify-end gap-1 opacity-70 mt-1 ${msg.type === 'sticker' ? 'bg-black/40 backdrop-blur-md px-1.5 py-0.5 rounded-full text-white' : ''}`}>
                                                                                {msg.is_ai_generated && (
                                                                                    <span className="text-[9px] font-bold bg-orange-500/20 text-orange-700 dark:text-orange-300 px-1 rounded mr-0.5">IA</span>
                                                                                )}
                                                                                <span className="text-[10px] leading-none">
                                                                                    {dayjs.utc(msg.created_at).local().format('HH:mm')}
                                                                                </span>
                                                                                {isOutbound && isPendingApproval && (
                                                                                    <span className="text-[9px] font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 px-1 rounded whitespace-nowrap" title="Redactado por la IA, sin enviar: espera aprobación en Tutor">
                                                                                        sin enviar
                                                                                    </span>
                                                                                )}
                                                                                {isOutbound && !isPendingApproval && (
                                                                                    <span className={`material-symbols-outlined text-[14px] leading-none block ${msg.status === 'read' ? 'text-blue-500' : ''}`}>
                                                                                        {isOptimistic ? 'schedule' : (msg.status === 'read' || msg.status === 'delivered') ? 'done_all' : 'check'}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Reacción — aparece bajo la burbuja pegada al borde */}
                                                        {currentReaction && (
                                                            <div className={`flex ${isOutbound ? 'justify-end mr-1' : 'justify-start ml-1'} -mt-2`}>
                                                                <span 
                                                                    className="text-[15px] bg-white dark:bg-[#2a3942] rounded-full shadow-md px-1.5 py-0.5 border border-gray-200 dark:border-gray-600 leading-none"
                                                                    style={{ animation: 'reactionIn 0.2s ease-out forwards' }}
                                                                >
                                                                    {currentReaction}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                })()
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Send failed error banner */}
                        {sendMutation.isError && (
                            <div className="mx-4 mb-2 z-10 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 text-xs px-3 py-2 rounded-lg">
                                Error al enviar el mensaje. Por favor reintenta.
                            </div>
                        )}

                        {/* Composer Area */}
                        <div className="bg-[#f0f2f5] dark:bg-[#202c33] p-3 flex flex-col gap-2 z-10 shrink-0 relative">
                            {/* Reply Preview Card */}
                            {replyingTo && (
                                <div className="flex items-center justify-between bg-white dark:bg-[#2a3942] border-l-[4px] border-primary rounded-lg p-2.5 shadow-sm text-left animate-in slide-in-from-bottom-2 fade-in duration-200">
                                    <div className="flex-1 min-w-0 pr-4">
                                        <p className="text-[11px] font-bold text-primary truncate">
                                            Responder a {replyingTo.direction === 'outbound' ? 'Tú' : (activeConversation?.contact?.full_name || activeConversation?.contact?.first_name || 'Cliente')}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5 italic">
                                            {replyingTo.body || (replyingTo.type === 'image' ? '📷 Foto' : replyingTo.type === 'video' ? '🎥 Video' : replyingTo.type === 'voice_note' ? '🎵 Nota de voz' : replyingTo.type === 'sticker' ? '💟 Sticker' : 'Archivo')}
                                        </p>
                                    </div>
                                    <button 
                                        onClick={() => setReplyingTo(null)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                                    >
                                        <span className="material-symbols-outlined text-[18px] block">close</span>
                                    </button>
                                </div>
                            )}

                            {/* Horizontal input row */}
                            <div className="flex items-end gap-2 w-full">
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    style={{ display: 'none' }} 
                                    onChange={handleFileSelect} 
                                />
                                {!isRecording && (
                                    <>
                                        {canSendMedia && (
                                            <button
                                                type="button"
                                                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors shrink-0 flex items-center justify-center h-10"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={isUploadingMedia}
                                                title="Adjuntar archivo o imagen"
                                            >
                                                <span className="material-symbols-outlined">attach_file</span>
                                            </button>
                                        )}

                                        {canUseCatalog && (
                                            <button
                                                type="button"
                                                className={`p-2 transition-colors shrink-0 flex items-center justify-center h-10 rounded-full ${
                                                    showCatalogDrawer
                                                        ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 font-bold'
                                                        : 'text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400'
                                                }`}
                                                onClick={() => setShowCatalogDrawer(!showCatalogDrawer)}
                                                title="Catálogo de Productos de WhatsApp"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">storefront</span>
                                            </button>
                                        )}

                                        {canUseStickers && (
                                            <div className="relative shrink-0">
                                                <button
                                                    type="button"
                                                    className={`p-2 transition-colors flex items-center justify-center h-10 rounded-full ${
                                                        showStickers
                                                            ? 'text-primary bg-primary/10 font-bold'
                                                            : 'text-gray-500 hover:text-primary'
                                                    }`}
                                                    onClick={() => { setShowStickers(!showStickers); setStickerError(null); }}
                                                    title="Stickers"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">emoji_emotions</span>
                                                </button>

                                                {showStickers && (
                                                    <StickerPicker
                                                        onPick={handlePickSticker}
                                                        onClose={() => setShowStickers(false)}
                                                        enviando={enviandoSticker}
                                                    />
                                                )}

                                                {stickerError && (
                                                    <div className="absolute bottom-[calc(100%+12px)] left-0 w-72 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed z-[110] shadow-lg">
                                                        {stickerError}
                                                        <button
                                                            type="button"
                                                            onClick={() => setStickerError(null)}
                                                            className="block mt-2 font-semibold underline"
                                                        >
                                                            Entendido
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}

                                {isRecording ? (
                                    <div className="flex-1 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 flex items-center justify-between border border-red-200 dark:border-red-800">
                                        <div className="flex items-center gap-2 text-red-500">
                                            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                                            <span className="font-medium">{dayjs().startOf('day').second(recordingTime).format('mm:ss')}</span>
                                        </div>
                                        <button onClick={() => stopRecording(true)} className="text-gray-400 hover:text-gray-600 transition text-sm flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[18px]">delete</span> Cancelar
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex-1 bg-white dark:bg-[#2a3942] rounded-lg border border-transparent focus-within:border-primary/30 transition-colors flex flex-col items-stretch relative">
                                        {/* Template Suggestions Popover */}
                                        {showSuggestions && (
                                            <div className="absolute bottom-[calc(100%+12px)] left-0 w-80 bg-white dark:bg-[#233138] rounded-2xl shadow-[0_-8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_-8px_30px_rgb(0,0,0,0.4)] border border-gray-100 dark:border-gray-700/50 overflow-hidden z-[100] animate-in slide-in-from-bottom-2 fade-in duration-200">
                                                <div className="p-3 border-b border-gray-50 dark:border-gray-700/50 bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur-md flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="material-symbols-outlined text-[16px] text-primary">description</span>
                                                        <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Plantillas</span>
                                                    </div>
                                                    <span className="text-[10px] text-gray-400 font-mono">@{suggestionQuery}</span>
                                                </div>
                                                <div className="max-h-[280px] overflow-y-auto no-scrollbar py-1">
                                                    {filteredTemplates.map((template, idx) => (
                                                        <button
                                                            key={template.id}
                                                            onClick={() => handleSuggestionSelect(template)}
                                                            className={`w-full text-left px-4 py-3 flex flex-col gap-1 transition-all
                                                                ${suggestionIndex === idx 
                                                                    ? 'bg-primary/10 dark:bg-primary/20 border-l-4 border-primary' 
                                                                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/40 border-l-4 border-transparent'}
                                                            `}
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-[13px] font-bold text-text-main dark:text-gray-100">@{template.keyword}</span>
                                                                <span className="text-[9px] text-gray-400 opacity-0 group-hover:opacity-100 uppercase font-bold">Seleccionar</span>
                                                            </div>
                                                            <span className="text-[12px] text-gray-500 dark:text-gray-400 line-clamp-1 italic">
                                                                "{template.content.substring(0, 60)}{template.content.length > 60 ? '...' : ''}"
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <textarea 
                                            ref={textareaRef}
                                            value={newMessage}
                                            onChange={handleInputChange}
                                            onKeyDown={handleKeyPress}
                                            onFocus={handleTyping}
                                            disabled={sendMutation.isPending || isUploadingMedia}
                                            className="w-full bg-transparent border-none text-[15px] resize-none focus:ring-0 max-h-32 px-4 py-3 placeholder:text-gray-400 dark:text-gray-200"
                                            placeholder="Escribe un mensaje..."
                                            rows={1}
                                            style={{ minHeight: '44px' }}
                                        />
                                    </div>
                                )}

                                {isRecording ? (
                                    <button 
                                        onClick={() => stopRecording(false)}
                                        className="bg-primary text-white p-2 rounded-full shadow-md hover:bg-primary/90 shrink-0 flex items-center justify-center h-10 w-10 transition-transform hover:scale-105"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">send</span>
                                    </button>
                                ) : (
                                    /* Sin texto el botón graba una nota de voz; si esa función está
                                       deshabilitada se queda como "enviar" inactivo. */
                                    <button
                                        id="send-message-btn"
                                        onClick={newMessage.trim() ? handleSend : (canUseVoiceNotes ? startRecording : undefined)}
                                        disabled={sendMutation.isPending || isUploadingMedia || (!newMessage.trim() && !canUseVoiceNotes)}
                                        className={`p-2 rounded-full shrink-0 flex items-center justify-center h-10 w-10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed
                                            ${newMessage.trim() && !sendMutation.isPending && !isUploadingMedia
                                                ? 'bg-primary text-white shadow-md hover:bg-primary/90'
                                                : 'bg-transparent text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                            }
                                        `}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">
                                            {sendMutation.isPending || isUploadingMedia
                                                ? 'sync'
                                                : (newMessage.trim() || !canUseVoiceNotes ? 'send' : 'mic')}
                                        </span>
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                ) : (
                    <div className="hidden md:flex flex-1 flex-col items-center justify-center bg-gray-50 dark:bg-background-dark/50 border-l border-gray-100 dark:border-gray-800">
                        <div className="w-64 h-64 flex items-center justify-center opacity-20 mb-10">
                            <span className="material-symbols-outlined text-[160px] text-primary">chat_bubble</span>
                        </div>
                        <h2 className="text-2xl font-light text-gray-700 dark:text-gray-300 mb-4">Tornado Web Chat</h2>
                        <p className="text-sm text-gray-500 max-w-md text-center leading-relaxed">
                            Selecciona una conversación para empezar a chatear.
                        </p>
                    </div>
                )}
            </div>
            <ContactModal 
                isOpen={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
                contact={activeConversation?.contact}
            />

            {/* Global floating preview tooltip to prevent scroll-container clipping */}
            {hoveredConvId && hoveredRect && (() => {
                const conv = conversations.find(c => c.id === hoveredConvId);
                if (!conv) return null;
                const body = conv.latest_message?.body || 'Nuevo chat iniciado...';
                const words = body.split(/\s+/);
                const truncatedBody = words.length > 30 ? words.slice(0, 30).join(' ') + '...' : body;

                return (
                    <div 
                        style={{
                            position: 'fixed',
                            top: `${hoveredRect.top + hoveredRect.height / 2}px`,
                            left: `${hoveredRect.right + 8}px`,
                            transform: 'translateY(-50%)',
                        }}
                        className="z-[9999] w-80 bg-white dark:bg-[#202c33] p-3 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 pointer-events-none text-left"
                    >
                        <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 mb-1 uppercase tracking-wider">
                            Vista previa
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-normal italic">
                            {truncatedBody}
                        </p>
                    </div>
                );
            })()}

            {/* ── WHATSAPP CATALOG DRAWER / SLIDE-OVER ── */}
            {showCatalogDrawer && (
                <div className="fixed inset-y-0 right-0 z-[100] w-full max-w-md bg-white dark:bg-[#111b21] shadow-2xl border-l border-gray-200 dark:border-gray-800 flex flex-col animate-in slide-in-from-right duration-250">
                    {/* Drawer Header */}
                    <div className="p-4 bg-gradient-to-r from-emerald-700 to-teal-700 text-white flex items-center justify-between shadow-md">
                        <div className="flex items-center gap-2.5">
                            <span className="material-symbols-outlined text-[22px]">storefront</span>
                            <div>
                                <h3 className="font-bold text-sm leading-tight">Catálogo de WhatsApp</h3>
                                <p className="text-[11px] text-emerald-100 opacity-90">Productos registrados en WhatsApp Business</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => refetchCatalog()}
                                disabled={catalogFetching}
                                title="Actualizar catálogo desde WhatsApp"
                                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <span className={`material-symbols-outlined text-[18px] block ${catalogFetching ? 'animate-spin' : ''}`}>sync</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowCatalogDrawer(false)}
                                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px] block">close</span>
                            </button>
                        </div>
                    </div>

                    {/* Anti-Ban & Cache Banner */}
                    <div className="bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2 border-b border-emerald-100 dark:border-emerald-900/30 flex items-center justify-between text-[11px] text-emerald-800 dark:text-emerald-300">
                        <div className="flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[14px]">shield</span>
                            <span>Modo Anti-Ban (Caché optimizada)</span>
                        </div>
                        {catalogResponse?.cached && (
                            <span className="text-[10px] font-mono opacity-80">En caché local</span>
                        )}
                    </div>

                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-black/20">
                        <div className="relative">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[18px]">search</span>
                            <input
                                type="text"
                                value={catalogQuery}
                                onChange={(e) => setCatalogQuery(e.target.value)}
                                placeholder="Buscar producto por nombre..."
                                className="w-full pl-9 pr-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-emerald-500 text-gray-800 dark:text-gray-200"
                            />
                        </div>
                    </div>

                    {/* Products List Container */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                        {catalogLoading ? (
                            <div className="h-48 flex flex-col items-center justify-center gap-2 text-gray-400 text-xs">
                                <span className="material-symbols-outlined text-[32px] text-emerald-500 animate-spin">sync</span>
                                <span>Cargando productos del catálogo...</span>
                            </div>
                        ) : filteredCatalog.length === 0 ? (
                            <div className="h-48 flex flex-col items-center justify-center gap-2 text-gray-400 text-center p-4">
                                <span className="material-symbols-outlined text-[40px] opacity-40">inventory_2</span>
                                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                    {catalogQuery ? 'No se encontraron productos coincidentes' : 'No se encontraron productos en la cuenta de WhatsApp Business'}
                                </p>
                                <p className="text-[11px] text-gray-400 max-w-xs">
                                    Asegúrate de tener productos publicados en tu aplicación WhatsApp Business móvil.
                                </p>
                            </div>
                        ) : (
                            filteredCatalog.map((prod) => {
                                const rawPrice = prod.price;
                                const numPrice = typeof rawPrice === 'number'
                                    ? (rawPrice > 1000 ? rawPrice / 1000 : rawPrice)
                                    : parseFloat(rawPrice || 0);

                                const isSendingThis = sendingProductId === prod.id;

                                return (
                                    <div key={prod.id} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1f2c34] overflow-hidden shadow-xs hover:border-emerald-500/40 transition-all group">
                                        <div className="flex gap-3 p-3">
                                            {/* Image or Placeholder */}
                                            {prod.image_url ? (
                                                <div className="w-20 h-20 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
                                                    <img src={resolveMediaUrl(prod.image_url)} alt={prod.title} className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
                                                </div>
                                            ) : (
                                                <div className="w-20 h-20 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                                    <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-[26px]">shopping_bag</span>
                                                </div>
                                            )}

                                            {/* Info */}
                                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                                                <div>
                                                    <div className="flex items-start justify-between gap-1">
                                                        <h4 className="font-bold text-xs text-gray-900 dark:text-gray-100 line-clamp-1">{prod.title}</h4>
                                                        {rawPrice != null && rawPrice !== '' && (
                                                            <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-extrabold text-[11px] whitespace-nowrap">
                                                                {prod.currency || '$'} {numPrice > 0 ? numPrice.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {prod.description && (
                                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5 leading-relaxed">
                                                            {prod.description}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="flex items-center gap-2 mt-2">
                                                    <button
                                                        type="button"
                                                        disabled={!activeConversationId || isSendingThis || sendMutation.isPending}
                                                        onClick={() => handleSendProductFromCatalog(prod)}
                                                        className="flex-1 py-1 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold flex items-center justify-center gap-1 transition-all disabled:opacity-50"
                                                    >
                                                        {isSendingThis ? (
                                                            <>
                                                                <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                                                                <span>Enviando...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="material-symbols-outlined text-[14px]">send</span>
                                                                <span>Enviar al cliente</span>
                                                            </>
                                                        )}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setNewMessage(`Me interesa este producto: ${prod.title}`);
                                                            setShowCatalogDrawer(false);
                                                        }}
                                                        title="Poner en la caja de texto"
                                                        className="p-1 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500 text-[11px]"
                                                    >
                                                        <span className="material-symbols-outlined text-[15px] block">edit_note</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* Delete Message Confirmation Modal */}
            {deletingMessage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-[#222e35] rounded-2xl p-6 shadow-2xl max-w-sm w-full border border-gray-200 dark:border-gray-700 text-left space-y-4 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-xl">delete</span>
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">¿Deseas eliminar este mensaje?</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {deletingMessage.direction === 'outbound'
                                        ? 'Puedes eliminarlo solo para ti o revocarlo para todos en WhatsApp.'
                                        : 'Este mensaje se eliminará de tu vista de chat.'}
                                </p>
                            </div>
                        </div>

                        {/* Inner preview of the message being deleted */}
                        <div className="bg-gray-50 dark:bg-[#111b21] p-3 rounded-xl border border-gray-100 dark:border-gray-800/80 text-xs text-gray-600 dark:text-gray-300 italic truncate max-h-16 overflow-hidden">
                            {deletingMessage.body || (deletingMessage.type === 'image' ? '📷 Foto' : deletingMessage.type === 'video' ? '🎥 Video' : 'Archivo adjunto')}
                        </div>

                        <div className="flex flex-col gap-2 pt-1">
                            {deletingMessage.direction === 'outbound' && (
                                <button
                                    onClick={() => handleConfirmDelete(true)}
                                    disabled={isDeleting}
                                    className="w-full py-2.5 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-xs transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                                >
                                    {isDeleting ? <span className="material-symbols-outlined text-sm animate-spin">sync</span> : null}
                                    Eliminar para todos
                                </button>
                            )}
                            <button
                                onClick={() => handleConfirmDelete(false)}
                                disabled={isDeleting}
                                className="w-full py-2.5 px-4 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold text-xs transition-colors flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                            >
                                {isDeleting ? <span className="material-symbols-outlined text-sm animate-spin">sync</span> : null}
                                Eliminar para mí
                            </button>
                            <button
                                onClick={() => setDeletingMessage(null)}
                                disabled={isDeleting}
                                className="w-full py-2 px-4 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xs font-medium transition-colors cursor-pointer"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
